import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, XCircle, CheckCircle2 } from "lucide-react";
import { IMAGES } from "@/lib/data";
import PageHead from "@/components/site/PageHead";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const PRINCIPLES = [
  { title: "Additive, not substitutive", text: "T-EMRP works with the doctrine, protocols, and command structures agencies already use." },
  { title: "Locally governed", text: "Clinical content is approved by a local medical director; operations by agency leads." },
  { title: "Observable", text: "Every claimed capability must be demonstrable in a scenario an outside evaluator can score." },
  { title: "Bounded in cost", text: "Sized to real overtime, apparatus, and facility constraints." },
  { title: "Falsifiable", text: "Designed so it can be shown not to work — and honest enough to report that if it happens." },
];

const NOT_LIST = [
  "Not a replacement for TECC or TCCC",
  "Not a national credential",
  "Not a substitute for medical direction",
  "Not a patient registry",
  "Not autonomous AI triage",
  "Not a claim of proven mortality reduction",
  "Not a federally endorsed program",
];

const STATUS = [
  "Evidence-backed institutional framework developed",
  "Proposed 18-month pilot architecture defined",
  "Preliminary governance model established",
  "Candidate readiness measures identified",
  "Institutional outreach materials under development",
  "Technical and institutional review opportunities being sought",
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHead
        chapter="02"
        eyebrow="About T-EMRP"
        titleLines={["An implementation framework,", "not a competing clinical doctrine."]}
        lede="T-EMRP inherits its clinical content from recognized U.S. guidance. Its contribution is the method by which institutions align, exercise, measure, and improve that content together."
        image={IMAGES.gearDetail}
      />

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Eyebrow>Mission</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-3xl text-white tracking-tight leading-tight">
            Convert existing doctrine into shared, measurable, interagency readiness.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed">
            To help law enforcement, fire/rescue, EMS, dispatch, and hospital partners convert existing active-threat medical doctrine into shared, measurable, interagency readiness — through a bounded, independently evaluated pilot.
          </p>
          <h3 className="mt-12 font-display font-bold text-xl text-white tracking-tight">Problem statement</h3>
          <p className="mt-4 text-sm text-slate-400 leading-relaxed">
            Evidence-based tactical medicine already exists in the United States: TCCC, its civilian adaptation TECC, national EMS education standards, NIMS, and NEMSIS together form substantial clinical and systems infrastructure. What is not uniformly operationalized across independent agencies is the connective work: shared training objectives, joint exercises, cross-agency handoffs, readiness measurement, and disciplined after-action learning.
          </p>
          <h3 className="mt-12 font-display font-bold text-xl text-white tracking-tight">Theory of change</h3>
          <p className="mt-4 text-sm text-slate-400 leading-relaxed">
            A shorter, better-coordinated interval between casualty contact and effective, protocol-consistent care depends on agencies that rarely train together being able to perform a rehearsed, measured sequence: agree on a shared crosswalk, train against it, exercise it jointly, measure the result, correct what fails, and replicate only where evidence supports it.
          </p>
          <div className="mt-8 border-l-2 border-signal bg-surface p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-3">Causal discipline</p>
            <p className="text-sm text-slate-300 leading-relaxed">
              A shorter simulated hemorrhage-control interval may support a conclusion about readiness. It does not, by itself, demonstrate a reduction in population mortality. Patient-outcome claims require longer, appropriately governed research controlling for case mix, transport, and hospital care.
            </p>
          </div>
        </Reveal>

        <div>
          <Reveal delay={0.1}>
            <Eyebrow>Principles</Eyebrow>
            <div className="mt-5 border border-line divide-y divide-line">
              {PRINCIPLES.map((p, i) => (
                <div key={i} data-testid={`principle-${i}`} className="group p-6 hover:bg-surface transition-colors">
                  <div className="flex items-start gap-4">
                    <span className="font-mono text-xs text-signal mt-1">0{i + 1}</span>
                    <div>
                      <h4 className="font-display font-bold text-white tracking-tight">{p.title}</h4>
                      <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{p.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 bg-surface border border-line p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-6">Current development status</p>
              <ul className="space-y-3">
                {STATUS.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle2 size={15} className="text-signal mt-0.5 shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-line bg-surface py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Eyebrow>Clinical and operational boundaries</Eyebrow>
            <ul className="mt-8 space-y-5 text-sm text-slate-300 leading-relaxed">
              <li className="flex gap-3"><span className="text-signal font-mono">→</span> T-EMRP does not replace TECC, TCCC, NIMS, state EMS rules, local protocols, or a physician medical director.</li>
              <li className="flex gap-3"><span className="text-signal font-mono">→</span> No participant performs any procedure outside the scope authorized by law, licensure, employer policy, credentialing, and medical direction.</li>
              <li className="flex gap-3"><span className="text-signal font-mono">→</span> The program's role is architecture, curriculum integration, simulation, training operations, and quality improvement. Independent U.S. clinical oversight remains mandatory throughout.</li>
              <li className="flex gap-3"><span className="text-signal font-mono">→</span> Any certification issued during the pilot is a local completion record — not a license, and not a nationally recognized credential.</li>
              <li className="flex gap-3"><span className="text-signal font-mono">→</span> Patient-level research requires applicable institutional, privacy, and human-subject review. The initial pilot relies on simulation and de-identified operational measures.</li>
            </ul>
            <h3 className="mt-12 font-display font-bold text-xl text-white tracking-tight">Relationship to existing standards</h3>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              T-EMRP integrates with six existing national assets — TECC/TCCC, EMS education standards, NIMS/ICS, NEMSIS, NEMSQA, and the 2024 National EMS Advisory Council recommendations — rather than duplicating any of them. During the pilot, the shared competency set is called a <strong className="text-white">Common Readiness Crosswalk</strong>, never a national standard.
            </p>
            <h3 className="mt-12 font-display font-bold text-xl text-white tracking-tight">Limitations</h3>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              T-EMRP has not yet demonstrated reliability across sites, persistence of training gains, cost-effectiveness, acceptability to U.S. agencies, or any effect on patient outcomes. Its proposed measures may require revision after feasibility testing. These limitations are stated here rather than left for a reviewer to discover independently.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-ink border border-line p-8 lg:p-10 lg:sticky lg:top-28">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-8">What T-EMRP is not</p>
              <ul className="space-y-4">
                {NOT_LIST.map((n, i) => (
                  <li key={i} data-testid={`not-item-${i}`} className="flex items-center gap-3 text-slate-200 font-display font-bold text-lg tracking-tight border-b border-line pb-4">
                    <XCircle size={16} className="text-signal shrink-0" /> {n}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap gap-4">
          <Link to="/contact" data-testid="about-cta-contact" className="inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
            Request More Information <ArrowUpRight size={15} />
          </Link>
          <Link to="/framework" data-testid="about-cta-framework" className="inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
            See the full framework <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </main>
  );
}
